import { Methods } from '../../api/methods.js';
import { loadCliConfig, parseDevice, resolvePaths, saveCliConfig } from '../../client/config.js';
import { scanDevices } from '../../client/resolver.js';
import { CredentialStore } from '../../crypto/storage.js';
import type { ParsedArgs } from '../args.js';
import { flag } from '../args.js';
import { CliError, classifyError, ExitCode } from '../errors.js';
import type { CommandResult } from '../output.js';
import { withClient } from './common.js';

export async function devicesCommand(args: ParsedArgs): Promise<CommandResult> {
  const action = args.positionals[1] ?? 'list';
  switch (action) {
    case 'list': {
      const paths = resolvePaths(args.options.configPath, args.options.credentialsPath);
      const config = loadCliConfig(paths.configPath);
      const store = new CredentialStore(paths.credentialsPath);
      const data = config.devices.map((device) => ({
        id: device.id,
        host: device.host,
        port: device.port,
        scheme: device.scheme ?? 'ws',
        platform: device.platform,
        version: device.version,
        apiKeyConfigured: device.apiKey !== undefined,
        isDefault: device.id === config.defaultDevice,
        paired: store.getCredentials(device.id, device.aliases) !== undefined,
      }));
      return { data, human: data.map((d) => `${d.id}${d.isDefault ? ' default' : ''}`).join('\n') };
    }
    case 'scan': {
      const devices = await scanDevices(args.options.timeoutSeconds * 1000);
      return {
        data: devices,
        human: devices.map((d) => `${d.id} ${d.txtRecord.platform ?? ''}`).join('\n'),
      };
    }
    case 'ping': {
      const data = await withClient(args.options, async (client) => ({
        device: client.device.id,
        version: client.info ?? (await client.request(Methods.Version)),
        health: await client.request(Methods.Health).catch(() => undefined),
      }));
      return { data, human: `OK ${data.device}` };
    }
    case 'default':
      return defaultCommand(args);
    default:
      throw new CliError(`Unknown devices action "${action}"`, ExitCode.Usage);
  }
}

async function defaultCommand(args: ParsedArgs): Promise<CommandResult> {
  const action = args.positionals[2] ?? 'show';
  const paths = resolvePaths(args.options.configPath, args.options.credentialsPath);
  const config = loadCliConfig(paths.configPath);
  switch (action) {
    case 'show':
      return {
        data: { defaultDevice: config.defaultDevice ?? null },
        human: config.defaultDevice ?? 'No default device set',
      };
    case 'set': {
      const raw = args.positionals[3] ?? flag(args.flags, 'device');
      if (!raw) throw new CliError('devices default set requires <host:port>', ExitCode.Usage);
      const device = parseDevice(raw);
      const devices = config.devices.some((d) => d.id === device.id)
        ? config.devices
        : [...config.devices, device];
      saveCliConfig({ ...config, devices, defaultDevice: device.id }, paths.configPath);
      return {
        data: { success: true, defaultDevice: device.id },
        human: `Default device set to ${device.id}`,
      };
    }
    case 'clear':
      saveCliConfig({ ...config, defaultDevice: undefined }, paths.configPath);
      return { data: { success: true }, human: 'Default device cleared' };
    default:
      throw new CliError(`Unknown devices default action "${action}"`, ExitCode.Usage);
  }
}

export async function stateCommand(args: ParsedArgs): Promise<CommandResult> {
  const data = await withClient(args.options, async (client) => {
    const [readers, activeMedia, tokens] = await Promise.all([
      client.request(Methods.Readers).catch(requestFailure),
      client.request(Methods.MediaActive).catch(requestFailure),
      client.request(Methods.Tokens).catch(requestFailure),
    ]);
    return {
      device: client.device.id,
      version: client.info,
      readers: summarizeReaders(readers),
      activeMedia,
      tokens: summarizeTokens(tokens),
    };
  });
  return { data, human: `State snapshot for ${data.device}` };
}

function requestFailure(error: unknown): {
  ok: false;
  error: string;
  kind?: string;
} {
  const classified = classifyError(error);
  const data = classified.data;
  const kind =
    data && typeof data === 'object' && 'kind' in data && typeof data.kind === 'string'
      ? data.kind
      : undefined;
  return { ok: false, error: classified.message, kind };
}

function summarizeReaders(value: unknown): unknown {
  if (!value || typeof value !== 'object') return value;
  const record = value as Record<string, unknown>;
  if (!Array.isArray(record.readers)) return value;
  return {
    count: record.readers.length,
    connected: record.readers.filter(
      (reader) =>
        reader &&
        typeof reader === 'object' &&
        (reader as Record<string, unknown>).connected === true,
    ).length,
    ids: record.readers
      .map((reader) =>
        reader && typeof reader === 'object'
          ? ((reader as Record<string, unknown>).id ?? (reader as Record<string, unknown>).readerId)
          : undefined,
      )
      .filter((id): id is string => typeof id === 'string'),
  };
}

function summarizeTokens(value: unknown): unknown {
  if (!value || typeof value !== 'object') return value;
  const record = value as Record<string, unknown>;
  if (record.ok === false && typeof record.error === 'string') return value;
  const active = Array.isArray(record.active) ? record.active : [];
  const last = record.last;
  return {
    activeCount: active.length,
    hasLast: last !== undefined && last !== null,
  };
}
